import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AlertCircle, AlertTriangle, ArrowLeft, ChevronRight, RefreshCw, SearchX, Sparkles } from 'lucide-react';
import CompanyGmpHero from '../components/gmp/company/CompanyGmpHero';
import { selectCompanyLogos } from '../store/selectors/companyLogos';
import CompanyKeyFigures from '../components/gmp/company/CompanyKeyFigures';
import CompanyIpoTimeline from '../components/gmp/company/CompanyIpoTimeline';
import CompanyGmpRange from '../components/gmp/company/CompanyGmpRange';
import CompanyGainEstimator from '../components/gmp/company/CompanyGainEstimator';
import OtherGmpIpos from '../components/gmp/company/OtherGmpIpos';
import AdSlot from '../components/advertisement/AdSlot';
import {
  fetchGmp,
  fetchGmpCompany,
  selectGmpBoardRows,
  selectGmpCompanyEntry,
} from '../store/slices/gmpSlice';
import { toGmpSlug } from '../utils/gmpDataMapper';
import { buildGmpSummary, mapApiItemToBoardRow, withDerivedFields } from '../utils/gmpBoard';

const DATA_MAX_AGE_MS = 60 * 1000;
const OTHER_IPOS_LIMIT = 6;

const PageSkeleton = () => (
  <div className="animate-pulse space-y-6" aria-label="Loading GMP details">
    <div className="h-52 rounded-3xl bg-slate-200" />
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
      <div className="space-y-6">
        <div className="h-60 rounded-2xl bg-slate-100" />
        <div className="h-40 rounded-2xl bg-slate-100" />
      </div>
      <div className="h-80 rounded-2xl bg-slate-100" />
    </div>
  </div>
);

const MessageCard = ({ icon: Icon, tone, title, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm px-6 py-12 text-center">
    <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto ${tone}`}>
      <Icon className="w-5 h-5" />
    </div>
    <h1 className="mt-4 text-lg font-extrabold text-slate-900">{title}</h1>
    <div className="mt-2 text-sm text-slate-500 max-w-md mx-auto">{children}</div>
  </div>
);

export const GmpCompanyPage = () => {
  const { companySlug = '' } = useParams();
  const slug = toGmpSlug(companySlug);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const entry = useSelector((state) => selectGmpCompanyEntry(state, slug));
  const boardRows = useSelector(selectGmpBoardRows);
  const listStatus = useSelector((state) => state.gmp.status);
  // The GMP feed carries no logo; this reuses one already loaded elsewhere
  const logos = useSelector(selectCompanyLogos);

  // Canonicalise hand-typed URLs such as /ipo-gmp/A-One Steels
  useEffect(() => {
    if (slug && slug !== companySlug) navigate(`/ipo-gmp/${slug}`, { replace: true });
  }, [slug, companySlug, navigate]);

  useEffect(() => {
    if (!slug) return;
    dispatch(fetchGmpCompany({ slug, maxAgeMs: DATA_MAX_AGE_MS }));
    // The list powers the sidebar and gives an instant first paint when arriving from the board
    dispatch(fetchGmp({ maxAgeMs: DATA_MAX_AGE_MS }));
  }, [dispatch, slug]);

  const listRow = useMemo(() => boardRows.find((row) => row.slug === slug) || null, [boardRows, slug]);
  const companyItem = entry?.item;
  const row = useMemo(
    () => (companyItem ? withDerivedFields(mapApiItemToBoardRow(companyItem)) : listRow),
    [companyItem, listRow]
  );

  const summary = useMemo(() => (row ? buildGmpSummary(row) : []), [row]);

  const otherRows = useMemo(
    () =>
      boardRows
        .filter((other) => other.slug && other.slug !== slug && other.hasGmp)
        .sort((a, b) => Number(a.isClosed) - Number(b.isClosed) || (b.gmpPercent ?? 0) - (a.gmpPercent ?? 0))
        .slice(0, OTHER_IPOS_LIMIT),
    [boardRows, slug]
  );

  // Companies whose name shares a word with the requested slug, for the not-found state
  const suggestions = useMemo(() => {
    const words = slug.split('-').filter((word) => word.length >= 3);
    if (!words.length) return [];
    return boardRows.filter((other) => words.some((word) => other.slug?.includes(word))).slice(0, 4);
  }, [boardRows, slug]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = row ? `${row.name} IPO GMP Today — Alambrix IPO` : 'IPO GMP — Alambrix IPO';
    return () => {
      document.title = previousTitle;
    };
  }, [row]);

  const status = entry?.status;
  const isLoading = !row && (!entry || status === 'loading');
  const notFound = !row && (!slug || (status === 'failed' && entry.notFound));
  const loadError = !row && status === 'failed' && !entry.notFound ? entry.error : null;
  const refreshFailed = Boolean(row && status === 'failed');

  const handleRefresh = () => {
    dispatch(fetchGmpCompany({ slug }));
  };

  let content;
  if (notFound) {
    content = (
      <MessageCard icon={SearchX} tone="bg-slate-100 text-slate-500" title="No GMP data for this IPO">
        <p>
          We couldn&apos;t find a grey market quote matching &ldquo;{companySlug}&rdquo;. The issue may have listed
          already or isn&apos;t tracked yet.
        </p>
        {suggestions.length > 0 && (
          <div className="mt-5">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Did you mean</p>
            <div className="flex flex-wrap justify-center gap-2 mt-2">
              {suggestions.map((suggestion) => (
                <Link
                  key={suggestion.id}
                  to={`/ipo-gmp/${suggestion.slug}`}
                  className="px-3 py-1.5 rounded-full border border-teal-200 bg-teal-50 text-xs font-bold text-[#008080] hover:bg-[#008080] hover:text-white transition-colors"
                >
                  {suggestion.name}
                </Link>
              ))}
            </div>
          </div>
        )}
        <Link
          to="/ipo-gmp"
          className="inline-flex items-center gap-1.5 mt-6 px-4 py-2 rounded-lg bg-[#008080] text-white text-xs font-bold hover:bg-[#006666] transition-colors"
        >
          Browse all IPO GMPs
        </Link>
      </MessageCard>
    );
  } else if (loadError) {
    content = (
      <MessageCard icon={AlertCircle} tone="bg-rose-50 text-rose-600" title="GMP details are temporarily unavailable">
        <p>{loadError}</p>
        <button
          type="button"
          onClick={handleRefresh}
          className="mt-5 px-4 py-2 rounded-lg bg-[#008080] text-white text-xs font-bold hover:bg-[#006666] transition-colors"
        >
          Try Again
        </button>
      </MessageCard>
    );
  } else if (isLoading) {
    content = <PageSkeleton />;
  } else {
    content = (
      <>
        {refreshFailed && (
          <div role="status" className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>Couldn&apos;t refresh this IPO just now ({entry.error}). Showing the most recent data we have.</span>
          </div>
        )}

        <CompanyGmpHero row={row} logoUrl={logos.lookup(row.name)} />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[minmax(0,1fr)_340px] gap-5 sm:gap-6 mt-5 sm:mt-6 items-start">
          <div className="space-y-5 sm:space-y-6 min-w-0">
            {/* GMP at a glance */}
            <section className="relative bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-6 overflow-hidden">
              <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#008080]" aria-hidden="true" />
              <h2 className="flex items-center gap-2 text-base font-extrabold text-slate-900">
                <Sparkles className="w-4 h-4 text-[#008080]" />
                GMP at a Glance
              </h2>
              <div className="mt-3 space-y-2 text-sm text-slate-600 leading-relaxed">
                {summary.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            </section>

            <CompanyKeyFigures row={row} />
            {/* IPO detail in-content ad (GET /api/ads -> 'incontent') */}
            <AdSlot slotId="incontent" />
            <CompanyIpoTimeline row={row} />
            <CompanyGmpRange row={row} />
          </div>

          <aside className="space-y-5 sm:space-y-6">
            <CompanyGainEstimator key={row.slug || row.id} row={row} />
            <OtherGmpIpos
              rows={otherRows}
              loading={!boardRows.length && (listStatus === 'idle' || listStatus === 'loading')}
            />
          </aside>
        </div>

        <p className="flex items-start gap-2 mt-8 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-[11px] sm:text-xs text-amber-900/80 leading-relaxed">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600" />
          <span>
            <strong className="font-bold text-amber-900">Disclaimer:</strong> GMP comes from unofficial grey market
            dealers and is only indicative. It can change quickly and does not guarantee the listing price. Always do
            your own due diligence and read the RHP before applying.
          </span>
        </p>
      </>
    );
  }

  return (
    <main className="flex-1 bg-slate-50/70">
      <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-4 sm:pt-6 pb-12 sm:pb-16">
        {/* Breadcrumb + actions */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 sm:mb-5">
          <nav
            className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap min-w-0"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-[#008080] transition-colors">
              Home
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <Link to="/ipo-gmp" className="hover:text-[#008080] transition-colors">
              Live IPO GMP
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-[#008080] font-semibold truncate">{row?.name || companySlug}</span>
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/ipo-gmp"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              GMP Board
            </Link>
            {/* {row && (
              <button
                type="button"
                onClick={handleRefresh}
                disabled={status === 'loading'}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-teal-200 bg-teal-50 text-xs font-bold text-[#008080] hover:bg-[#008080] hover:text-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${status === 'loading' ? 'animate-spin' : ''}`} />
                {status === 'loading' ? 'Refreshing' : 'Refresh'}
              </button>
            )} */}
          </div>
        </div>

        {content}
      </div>
    </main>
  );
};

export default GmpCompanyPage;
